/**
 * Keeps the messages badge current without polling away from the inbox.
 *
 * The API has no unread-count endpoint for conversations — only the full
 * `Conversation/List` page — so the badge is refreshed on the navigations a
 * member makes anyway (sign-in and the main destinations below) instead of on a
 * timer. An idle member receives no refresh, which is the point: nothing is
 * being read, so nothing needs recounting.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useMessages } from '@wlb/shared/context/MessageContext';

/**
 * Destinations that trigger a silent recount. `/messages` is absent on purpose:
 * the inbox fetches the list itself, with the member's search and filter.
 */
const BADGE_SYNC_PATHS = ['/', '/buddies', '/notifications', '/blog'];

/**
 * Floor between two recounts. Without it, moving between two of these
 * destinations (or a back/forward burst) would fetch the list every few
 * hundred milliseconds for a number that changes far more slowly.
 */
const MIN_SYNC_INTERVAL_MS = 30000;

/** The inbox owns the list on this route, so a badge recount would fight it. */
const INBOX_PATH = '/messages';

const isDocumentVisible = (): boolean => document.visibilityState === 'visible';

export function useConversationBadgeSync(): void {
  const { pathname } = useLocation();
  const { fetchConversations } = useMessages();
  const lastSyncRef = useRef(0);
  /** The provider mounts once a session exists, so the first run is sign-in. */
  const syncedOnceRef = useRef(false);

  useEffect(() => {
    if (pathname === INBOX_PATH) return;

    const isFirstRun = !syncedOnceRef.current;
    if (!isFirstRun && !BADGE_SYNC_PATHS.includes(pathname)) return;
    if (!isDocumentVisible()) return;

    const now = Date.now();
    if (!isFirstRun && now - lastSyncRef.current < MIN_SYNC_INTERVAL_MS) return;
    syncedOnceRef.current = true;
    lastSyncRef.current = now;

    // Unfiltered, so the count covers every thread rather than the slice the
    // inbox last searched for, and silent so no screen shows a loader for it.
    fetchConversations({ silent: true, searchTerm: '', filter: 'All' }).catch(
      () => undefined,
    );
  }, [pathname, fetchConversations]);
}
